import React, { useEffect, useState } from "react";
import { Button, Checkbox, FormControlLabel, FormGroup, Grid, Step, StepLabel, Stepper, Switch, Container } from "@material-ui/core";
import styled from "styled-components";
import {
  CTextField,
  CSelect,
  CAutocomplete,
  CDialog,
  HFlex,
  Pane,
  ProgressButton,
  Text,
  DialogSuccessNotice,
  useErrorContext,
  ErrorBox,
  MultiSelect,
} from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import { Country, isUsMarket, TimeZone } from "utils/market";
import { capitalizeFirstLetter } from "utils/misc";
import { css } from "@emotion/css";
import { useStates } from "shared/ValueTable";
import { useMountState } from "utils/hooks";
import usePayers from "routes/patientSearch/queries/usePayers";
import useInviteTherapist, { Vars } from "routes/therapistSearch/queries/useInviteTherapist";
import { useHCPs, supportApiBase } from "api/hooks/useHCPs";
import { useHealthCareOrganizations } from "routes/healthCareOrganizations/queries/useHealthCareOrganizations";
import { HealthCareOrganizationForm } from "routes/healthCareOrganizations/components/HealthCareOrganizationForm";
import api from "api";

interface Props {
  onClose: () => void;
}

const countriesEU = [
  { value: Country.UK, label: "United Kingdom" },
  { value: Country.SE, label: "Sweden" },
];

const timeZonesEU = [
  { value: TimeZone.London, label: "London" },
  { value: TimeZone.Stockholm, label: "Stockholm" },
];

const timeZonesUS = [
  { value: TimeZone.Pacific, label: "Pacific Time" },
  { value: TimeZone.Mountain, label: "Mountain Time" },
  { value: TimeZone.Central, label: "Central Time" },
  { value: TimeZone.Eastern, label: "Eastern Time" },
];

const professionsEU = [
  { value: "physical_therapist", label: "Physical Therapist" },
  { value: "occupational_therapist", label: "Occupational Therapist" },
  { value: "speech_therapist", label: "Speech Therapist" },
  { value: "wellness_therapist", label: "Wellness Therapist" },
];

const professionsUS = [
  { value: "physical_therapist", label: "Physical Therapist" },
  { value: "occupational_therapist", label: "Occupational Therapist" },
  { value: "physical_therapist_assistant", label: "Physical Therapist Assistant" },
  { value: "speech_therapist", label: "Speech Therapist" },
  { value: "wellness_therapist", label: "Wellness Therapist" },
];

const roleOptions = [
  { value: "therapist", label: "Therapist" },
  { value: "hcp_admin", label: "HCP Admin" },
];

const therapistTypeOptions = [
  { value: "virtual", label: "Virtual" },
  { value: "clinical", label: "Clinical" },
  { value: "hybrid", label: "Hybrid" },
];

const treatmentEnrollments = [
  "hip_treatment_enrollment_enabled",
  "knee_treatment_enrollment_enabled",
  "back_treatment_enrollment_enabled",
  "hand_treatment_enrollment_enabled",
  "shoulder_treatment_enrollment_enabled",
  "neck_treatment_enrollment_enabled",
  "foot_treatment_enrollment_enabled",
  "elbow_treatment_enrollment_enabled",
  "lower_leg_treatment_enrollment_enabled",
  "wrist_treatment_enrollment_enabled",
  "fall_prevention_treatment_enrollment_enabled",
  "osteoporosis_treatment_enrollment_enabled",
] as const;

const featureFlagOptions = isUsMarket
  ? ([
      { key: "rtm", flag: "RTM_ENABLED" },
      { key: "hep", flag: "HEP_ENABLED" },
      { key: "demo_mode", flag: "DEMO_MODE" },
    ] as const)
  : ([{ key: "demo_mode", flag: "DEMO_MODE" }] as const);

const parseJointName = (joint: string) => {
  const parts = joint.split("_");
  if (parts[1] !== "treatment") {
    return `${parts[0]} ${parts[1]}`;
  }
  return parts[0];
};

const StyledFormGroup = styled(FormGroup)`
  justify-content: space-evenly;
`;

export const InviteTherapistForm: React.VFC<Props> = ({ onClose }) => {
  const form = useForm<Vars>();
  const { t } = useTranslation();
  const { mutateAsync, isLoading, isSuccess } = useInviteTherapist();
  const setError = useErrorContext();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState<string>();
  const [therapist, setTherapist] = useState<{ external_uuid: string } | null>(null);
  const [hcp, setHcp] = useState<{ name: string } | null>(null);
  const [payers, setPayers] = useState<{ label: string | undefined; value: number }[] | null>([]);
  const [joints, setJoints] = useState<Record<string, boolean>>(
    Object.fromEntries(treatmentEnrollments.map(j => [j, false]))
  );
  const [flags, setFlags] = useState<Record<string, boolean>>(
    Object.fromEntries(featureFlagOptions.map(f => [f.key, false]))
  );
  const [hcoDialogOpen, setHcoDialogOpen] = useState(false);
  const [createdHco, setCreatedHco] = useState<{ id: number; name: string } | null>(null);
  const licenses = useStates();
  const { data: hcps } = useHCPs();
  const { data: hcos } = useHealthCareOrganizations();
  const mappedHCOs =
    hcos
      ?.map(hco => ({ value: hco.id, label: hco.name }))
      ?.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())) || [];
  const mappedHCPs =
    hcps
      ?.map(hcp => ({ value: hcp.name ?? "", label: hcp.name ?? "" }))
      ?.sort((a, b) => (a.label ?? "").toLowerCase().localeCompare((b.label ?? "").toLowerCase())) || [];
  const getMountState = useMountState();

  const selectedCountry = form.watch("country") as Country | undefined;
  const country = isUsMarket ? Country.US : (selectedCountry ?? Country.SE);
  const { data: dataPayers, isSuccess: isSuccessPayers } = usePayers(country);

  const createNewHCP = form.watch("health_care_provider_new");
  const availableForPatients = form.watch("available_for_patients");

  useEffect(() => {
    if (isSuccessPayers && dataPayers) {
      setPayers(dataPayers.map(x => ({ value: x.id, label: x.name })));
    }
  }, [dataPayers, isSuccessPayers]);

  const [step1Error, setStep1Error] = useState(false);

  const goNext = () => {
    const v = form.getValues() as any;
    const empty = (val: any) => !val || (Array.isArray(val) && val.length === 0) || val === "";
    const required = ["email", "timezone", "first_name", "last_name", "profession", "roles", "therapist_type"];
    if (isUsMarket) required.push("us_licenses");
    else required.push("country");
    const valid = required.every(f => !empty(v[f]));
    setStep1Error(!valid);
    if (valid) setStep(2);
  };

  const submit = form.handleSubmit(async fields => {
    setEmail(fields.email);
    const isNewHCP = !!fields.health_care_provider_new;
    const newHCP = isNewHCP
      ? fields.health_care_provider_name
      : hcps?.find(hcp => hcp.name === fields.health_care_provider_name)?.name;

    // Resolve HCO ID: prefer newly created HCO, then form selection
    const hcoId = createdHco?.id ?? (fields.health_care_organization_id ? Number(fields.health_care_organization_id) : undefined);

    try {
      const response = await mutateAsync({
        ...fields,
        ...joints,
        available_for_patients: !!fields.available_for_patients,
        health_care_provider_new: isNewHCP,
        health_care_provider_name: newHCP ? newHCP : fields.health_care_provider_name,
        health_care_organization_id: hcoId,
      });
      if (!getMountState()) return;

      const res = await response.json();
      const hcpId = res.health_care_provider?.id;

      // Post-invite: set HCP feature flags via Go API
      if (isNewHCP && hcpId) {
        const featureFlags = featureFlagOptions.map(f => ({
          flag: f.flag,
          enabled: !!flags[f.key],
        }));
        for (const { flag, enabled } of featureFlags) {
          if (enabled) {
            await api.call(`/health_care_providers/${hcpId}/feature_flags`, {
              method: "PUT",
              data: { flag, enabled },
              root: supportApiBase,
            });
          }
        }
      }

      setHcp({ name: res.health_care_provider.name });
      setTherapist({ external_uuid: res.external_uuid });
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (isSuccess && hcp && therapist)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${email}. HCP ${hcp.name} UUID ${therapist.external_uuid}`}
        onClose={onClose}
      />
    );

  return (
    <Container>
      <Text heading bold mb={0} id="invite-pt">
        {t("form.invite_therapist")}
      </Text>
      <Stepper activeStep={step - 1} alternativeLabel style={{ padding: "12px 0" }}>
        <Step>
          <StepLabel>Therapist Details</StepLabel>
        </Step>
        <Step>
          <StepLabel>Organization</StepLabel>
        </Step>
      </Stepper>
      <FormProvider {...form}>
        <form noValidate onSubmit={submit}>
          {/* Step 1: Therapist Details */}
          <div style={{ display: step === 1 ? undefined : "none" }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <CTextField name="first_name" autoFocus label={t("form.first_name")} margin="dense" size="small" fullWidth required />
              </Grid>
              <Grid item xs={12}>
                <CTextField name="last_name" label={t("form.last_name")} margin="dense" size="small" fullWidth required />
              </Grid>
              <Grid item xs={12}>
                <CTextField required name="email" label={t("form.email")} type="email" margin="dense" size="small" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <CSelect
                  name="profession"
                  label={t("form.profession")}
                  items={isUsMarket ? professionsUS : professionsEU}
                  margin="dense"
                  size="small"
                  required
                  fullWidth
                  defaultValue={"physical_therapist"}
                >
                  {({ label }) => <Text>{label}</Text>}
                </CSelect>
              </Grid>
              <Grid item xs={12}>
                <MultiSelect
                  getOptionLabel={(opt: { value: string; label: string }) => opt.label}
                  getOptionValue={(opt: { value: string; label: string }) => opt.value}
                  name="roles"
                  options={roleOptions}
                  label={t("form.roles")}
                  margin="dense"
                  required
                  defaultValue={["therapist"]}
                />
              </Grid>
              <Grid item xs={12}>
                <CSelect
                  name="therapist_type"
                  label={t("form.therapist_type")}
                  items={therapistTypeOptions}
                  margin="dense"
                  size="small"
                  fullWidth
                  required
                  defaultValue="virtual"
                >
                  {({ label }: { label: string }) => <Text>{label}</Text>}
                </CSelect>
              </Grid>
              <Grid item xs={12}>
                <CSelect
                  name="timezone"
                  label={t("form.home_timezone")}
                  items={isUsMarket ? timeZonesUS : timeZonesEU}
                  margin="dense"
                  size="small"
                  required
                  fullWidth
                  defaultValue={!isUsMarket ? TimeZone.Stockholm : null}
                >
                  {({ label }) => <Text>{label}</Text>}
                </CSelect>
              </Grid>
              {!isUsMarket && (
                <Grid item xs={12}>
                  <CSelect
                    name="country"
                    label={t("form.country")}
                    items={countriesEU}
                    margin="dense"
                    size="small"
                    required
                    fullWidth
                    defaultValue={Country.SE}
                  >
                    {({ label }) => <Text>{label}</Text>}
                  </CSelect>
                </Grid>
              )}
              {isUsMarket && (
                <Grid item xs={12}>
                  <MultiSelect
                    getOptionLabel={({ value, code }) => `${value} (${code})`}
                    getOptionValue={opt => opt.code}
                    loading={licenses.isLoading}
                    noOptionsText={licenses.error ? <ErrorBox error={licenses.error} /> : ""}
                    name="us_licenses"
                    options={licenses.data ?? []}
                    label={t`form.state_licenses`}
                    required
                    margin="dense"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <label role="button" className={styles.label}>
                  <Text small light>
                    {t("form.available_for_patients")}
                  </Text>
                  <Text small ml="auto">
                    {availableForPatients ? t("buttons.yes") : t("buttons.no")}
                  </Text>
                  <Switch color="primary" inputRef={form.register} name="available_for_patients" />
                </label>
              </Grid>
            </Grid>
            {step1Error && (
              <Text small paragraph mb={0} mt={1} style={{ color: "#f44336" }}>
                Please fill in all required fields
              </Text>
            )}
            <HFlex justifyContent="flex-end" my={3}>
              <Button variant="contained" color="primary" onClick={goNext}>
                {t("buttons.next")}
              </Button>
            </HFlex>
          </div>

          {/* Step 2: Organization & Configuration */}
          <div style={{ display: step === 2 ? undefined : "none" }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                {!createNewHCP && (
                  <CAutocomplete
                    name="health_care_provider_name"
                    label={t("form.health_care_provider")}
                    options={mappedHCPs}
                    getOptionLabel={(opt: { label: string }) => opt.label}
                    getOptionValue={(opt: { value: string }) => opt.value}
                    required
                    margin="dense"
                  />
                )}
                {createNewHCP && (
                  <CTextField
                    name="health_care_provider_name"
                    label={t("form.health_care_provider")}
                    margin="dense"
                    size="small"
                    required={!!createNewHCP}
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox inputRef={form.register} name="health_care_provider_new" color="primary" />}
                  label={<Text small light>{t`form.create_new_health_care_provider`}</Text>}
                />
              </Grid>

              {createNewHCP && payers && (
                <Grid item xs={12}>
                  <CAutocomplete
                    name="health_care_provider_invitation_payer_id"
                    label={t("form.health_care_provider_invitation_payer")}
                    options={payers ?? []}
                    getOptionLabel={(opt: { label: string | undefined }) => opt.label ?? ""}
                    getOptionValue={(opt: { value: number }) => opt.value}
                    required
                    margin="dense"
                  />
                </Grid>
              )}
              {createNewHCP && (
                <Grid item xs={12}>
                  {createdHco ? (
                    <HFlex alignItems="center" mt={1}>
                      <Text small>
                        {t("form.health_care_organization")}: <b>{createdHco.name}</b>
                      </Text>
                      <Button size="small" onClick={() => setCreatedHco(null)}>
                        {t("buttons.change")}
                      </Button>
                    </HFlex>
                  ) : (
                    <>
                      <CAutocomplete
                        name="health_care_organization_id"
                        label={t("form.health_care_organization")}
                        options={mappedHCOs}
                        getOptionLabel={(opt: { label: string }) => opt.label}
                        getOptionValue={(opt: { value: number }) => opt.value}
                        margin="dense"
                      />
                      <Button size="small" color="primary" onClick={() => setHcoDialogOpen(true)}>
                        + {t("form.create_new_health_care_organization")}
                      </Button>
                    </>
                  )}
                </Grid>
              )}
            </Grid>
            <Text paragraph caption secondary mt={2}>
              {t("form.treatment_enrollments")}
            </Text>
            <Pane dark p={1}>
              <StyledFormGroup row>
                {treatmentEnrollments.map(joint => (
                  <FormControlLabel
                    key={joint}
                    labelPlacement="start"
                    label={<Text caption>{capitalizeFirstLetter(parseJointName(joint))}</Text>}
                    control={
                      <Switch
                        checked={!!joints[joint]}
                        onChange={() => setJoints(prev => ({ ...prev, [joint]: !prev[joint] }))}
                        color="primary"
                      />
                    }
                  />
                ))}
                <FormControlLabel
                  key="all_joints"
                  labelPlacement="start"
                  label={<Text caption>{t("therapist_profile.all_joints")}</Text>}
                  control={
                    <Switch
                      checked={treatmentEnrollments.every(j => !!joints[j])}
                      onChange={() => {
                        const newState = !treatmentEnrollments.every(j => joints[j]);
                        setJoints(Object.fromEntries(treatmentEnrollments.map(j => [j, newState])));
                      }}
                      color="primary"
                    />
                  }
                />
              </StyledFormGroup>
            </Pane>
            {createNewHCP && (
              <>
                <Text paragraph caption secondary mt={2}>
                  {t("form.feature_flags")}
                </Text>
                <Pane dark p={1}>
                  <StyledFormGroup row>
                    {featureFlagOptions.map(({ key, flag }) => (
                        <FormControlLabel
                          key={key}
                          labelPlacement="start"
                          label={<Text caption>{t(`form.${key}`)}</Text>}
                          control={
                            <Switch
                              checked={!!flags[key]}
                              onChange={() => setFlags(prev => ({ ...prev, [key]: !prev[key] }))}
                              color="primary"
                            />
                          }
                        />
                    ))}
                  </StyledFormGroup>
                </Pane>
              </>
            )}
            <HFlex justifyContent="space-between" my={3}>
              <Button onClick={() => setStep(1)}>{t("buttons.previous")}</Button>
              <ProgressButton loading={isLoading} endIcon={<Send width={20} />}>
                {t("buttons.send_invitation")}
              </ProgressButton>
            </HFlex>
          </div>
        </form>
      </FormProvider>

      <CDialog maxWidth="sm" fullWidth open={hcoDialogOpen} onClose={() => setHcoDialogOpen(false)}>
        <HealthCareOrganizationForm
          onSuccess={created => {
            if (created) setCreatedHco({ id: created.id, name: created.name });
            setHcoDialogOpen(false);
          }}
          onCancel={() => setHcoDialogOpen(false)}
        />
      </CDialog>
    </Container>
  );
};

const styles = {
  label: css`
    display: flex;
    align-items: center;
  `,
};
